import { TEAM, FAQS, CLINIC } from "../data/content";
import { Link } from "react-router-dom";
import { Star, Activity, HeartHandshake, ShieldCheck, ArrowRight } from "lucide-react";
import WhatsAppButton from "../components/site/WhatsAppButton";
import Seo, { faqLd } from "../lib/seo";

export function About() {
  return (
    <div>
      <section className="container-wide py-16">
        <div className="max-w-3xl">
          <div className="eyebrow mb-3">About Physio Hyve</div>
          <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">A modern rehabilitation clinic where care is built around <span className="text-brand-terracotta">you</span>.</h1>
          <p className="text-brand-ink2 mt-6 text-lg">
            Physio Hyve brings together personalised assessment, clinical reasoning, hands-on care, exercise-based rehabilitation and appropriate advanced techniques — to help people in Nangloi and West Delhi move better, recover function and return to what matters.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            { Icon: HeartHandshake, title: "Human first", body: "We listen carefully. We explain clearly. We build trust before treatment." },
            { Icon: Activity, title: "Evidence-informed", body: "Our decisions blend current research, clinical experience and your goals." },
            { Icon: ShieldCheck, title: "Honest about outcomes", body: "We don't guarantee cures. We give you a realistic path forward." },
          ].map(({ Icon, title, body }) => (
            <div key={title} className="card-soft p-6">
              <div className="w-10 h-10 rounded-xl bg-brand-cream text-brand-olive flex items-center justify-center mb-3"><Icon size={18}/></div>
              <div className="font-heading text-lg text-brand-ink">{title}</div>
              <p className="text-sm text-brand-ink2 mt-2">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Meet the Team strip */}
      <section className="container-wide pb-4">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="eyebrow mb-2">Meet the team</div>
            <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
              Led by clinicians with real experience.
            </h2>
          </div>
          <Link to="/team" className="btn-outline" data-testid="about-team-link">
            See all clinicians <ArrowRight size={14}/>
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {TEAM.filter((m) => m.href).map((m) => (
            <Link
              key={m.slug}
              to={m.href}
              data-testid={`about-team-${m.slug}`}
              className="card-soft overflow-hidden group hover:shadow-[0_16px_50px_rgb(0,0,0,0.08)] transition-shadow"
            >
              <div className="flex items-stretch gap-4">
                <div className="w-28 h-32 shrink-0 bg-brand-cream overflow-hidden">
                  <img src={m.photo} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="py-4 pr-4">
                  <div className="font-heading text-lg text-brand-ink group-hover:text-brand-olive leading-tight">{m.name}</div>
                  <div className="text-xs text-brand-terracotta mt-1">{m.title}</div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {m.focus.slice(0, 2).map((f) => (
                      <span key={f} className="text-[10px] bg-brand-cream text-brand-olive rounded-full px-2 py-0.5">{f}</span>
                    ))}
                  </div>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs text-brand-olive">
                    View profile <ArrowRight size={12}/>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Inside our clinic — real interior photos */}
      <section className="container-wide pt-14 pb-4" data-testid="about-clinic-tour">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="eyebrow mb-2">Inside our clinic</div>
            <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight max-w-2xl">
              A calm, well-equipped space built for real recovery.
            </h2>
            <p className="text-brand-ink2 mt-3 text-sm max-w-2xl">
              From private treatment rooms to a full rehabilitation gym and modern equipment — every square foot is planned around the way patients actually recover.
            </p>
          </div>
          <Link to="/contact" className="btn-outline" data-testid="about-clinic-visit">
            Plan your visit <ArrowRight size={14}/>
          </Link>
        </div>

        <div className="grid grid-cols-12 gap-3 md:gap-4">
          {/* Row 1: reception hero + front desk */}
          <ClinicPhoto
            src="/clinic/reception.jpg"
            alt="Reception and registration desk at Physio Hyve Clinic Nangloi with the Physio Hyve signboard"
            label="Reception & registration"
            className="col-span-12 md:col-span-8 aspect-[16/9]"
            testid="clinic-photo-reception"
          />
          <ClinicPhoto
            src="/clinic/front-desk.webp"
            alt="Front desk at Physio Hyve Clinic with Dr. Amit Sharma working"
            label="Front desk"
            className="col-span-12 md:col-span-4 aspect-[4/5] md:aspect-auto"
            testid="clinic-photo-front-desk"
          />

          {/* Row 2: treatment + rehab + waiting */}
          <ClinicPhoto
            src="/clinic/treatment-room.webp"
            alt="Private physiotherapy treatment room at Physio Hyve Clinic with adjustable treatment couch and TECAR therapy unit"
            label="Private treatment room"
            className="col-span-12 md:col-span-5 aspect-[4/3]"
            testid="clinic-photo-treatment-room"
          />
          <ClinicPhoto
            src="/clinic/rehab-gym.webp"
            alt="Rehabilitation gym at Physio Hyve Clinic with agility ladders, cable machine and treatment couches"
            label="Rehabilitation gym"
            className="col-span-6 md:col-span-4 aspect-[4/3]"
            testid="clinic-photo-rehab-gym"
          />
          <ClinicPhoto
            src="/clinic/waiting-area.webp"
            alt="Patient waiting area with seating bench at Physio Hyve Clinic"
            label="Waiting area"
            className="col-span-6 md:col-span-3 aspect-[4/3] md:aspect-auto"
            testid="clinic-photo-waiting"
          />

          {/* Row 3: kids + tecar + shockwave + strength */}
          <ClinicPhoto
            src="/clinic/kids-area.webp"
            alt="Pediatric physiotherapy play area at Physio Hyve Clinic with trampoline, climbing frames and ride-on toys"
            label="Pediatric play area"
            className="col-span-6 md:col-span-3 aspect-square"
            testid="clinic-photo-kids"
          />
          <ClinicPhoto
            src="/clinic/tecar-therapy.webp"
            alt="i-TECAR therapy machine used at Physio Hyve Clinic for pain relief and tissue recovery"
            label="TECAR therapy"
            className="col-span-6 md:col-span-3 aspect-square"
            testid="clinic-photo-tecar"
          />
          <ClinicPhoto
            src="/clinic/shockwave-therapy.webp"
            alt="Shockwave Pro-1000 therapy device at Physio Hyve Clinic for tendinopathy and chronic pain"
            label="Shockwave therapy"
            className="col-span-6 md:col-span-3 aspect-square"
            testid="clinic-photo-shockwave"
          />
          <ClinicPhoto
            src="/clinic/strength-area.webp"
            alt="Strength and conditioning area at Physio Hyve Clinic with cable machine, treadmill and exercise cycle"
            label="Strength area"
            className="col-span-6 md:col-span-3 aspect-square"
            testid="clinic-photo-strength"
          />
        </div>
      </section>

      <section className="bg-brand-cream/50 py-16 mt-10">
        <div className="container-wide grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl text-brand-ink">Our commitment</h2>
            <p className="text-brand-ink2 mt-4">
              Every patient deserves a clinician who understands their specific condition. We invest time in the first assessment so the entire recovery journey has a solid foundation.
            </p>
          </div>
          <div className="card-soft p-6">
            <div className="flex items-center gap-2 text-sm text-brand-ink2 mb-2">
              <Star className="fill-brand-terracotta text-brand-terracotta" size={16}/> {CLINIC.rating} · {CLINIC.reviewsCount} Google reviews
            </div>
            <p className="text-brand-ink text-lg">Our patients are our strongest voice. We're grateful for the community that trusts us with their recovery.</p>
            <div className="mt-4 flex gap-3">
              <Link to="/team" className="btn-primary">Meet Our Team</Link>
              <WhatsAppButton>Talk to us</WhatsAppButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ClinicPhoto({ src, alt, label, className = "", testid }) {
  return (
    <figure
      data-testid={testid}
      className={`relative overflow-hidden rounded-[18px] bg-brand-cream group ${className}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
      />
      <figcaption className="absolute inset-x-0 bottom-0 p-3 md:p-4 bg-gradient-to-t from-black/60 via-black/20 to-transparent text-white text-xs md:text-sm font-medium tracking-wide">
        {label}
      </figcaption>
    </figure>
  );
}

export function Team() {
  return (
    <div className="container-wide py-16">
      <div className="max-w-2xl">
        <div className="eyebrow mb-3">Meet our team</div>
        <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">Clinicians who care about your recovery.</h1>
        <p className="text-brand-ink2 mt-5">Some profile details are placeholders until administrator verification — this page is designed to be updated as full clinician information is confirmed.</p>
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {TEAM.map((m) => (
          <div key={m.slug} className="card-soft overflow-hidden" data-testid={`team-${m.slug}`}>
            <div className="aspect-[4/5] bg-brand-cream overflow-hidden">
              <img src={m.photo} alt={m.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-6">
              <div className="font-heading text-xl text-brand-ink">{m.name}</div>
              <div className="text-sm text-brand-terracotta mt-1">{m.title}</div>
              <p className="text-sm text-brand-ink2 mt-3">{m.bio}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {m.focus.map((f) => (
                  <span key={f} className="text-xs bg-brand-cream text-brand-olive rounded-full px-2.5 py-1">{f}</span>
                ))}
              </div>
              {m.href && (
                <Link
                  to={m.href}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm text-brand-olive hover:underline"
                  data-testid={`team-link-${m.slug}`}
                >
                  Learn more <ArrowRight size={14} />
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FirstVisit() {
  return (
    <div className="container-wide py-16 max-w-4xl">
      <div className="eyebrow mb-3">Your first visit</div>
      <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">What to expect at Physio Hyve.</h1>
      <div className="prose-hyve mt-8">
        <h2>Do I need a referral?</h2>
        <p>No. You can book an assessment directly. If you have a referral letter or reports, bring them along — they help.</p>
        <h2>What should I bring or wear?</h2>
        <ul>
          <li>Comfortable clothing you can move in</li>
          <li>Previous investigations (MRI, X-ray, blood tests) if available</li>
          <li>A list of current medications</li>
          <li>A friend or family member if that helps you</li>
        </ul>
        <h2>How long does assessment take?</h2>
        <p>Initial assessments typically take 45–60 minutes. This includes conversation, movement testing, clinical reasoning and a discussion of what to expect.</p>
        <h2>Will treatment start on the first visit?</h2>
        <p>Usually yes — where clinically appropriate. But the priority is a proper assessment before any treatment technique is chosen.</p>
        <h2>How many sessions will I need?</h2>
        <p>This depends on your specific condition, assessment findings, goals and how you respond. We share a realistic plan after your first visit — not before.</p>
      </div>
      <div className="mt-8 flex gap-3">
        <Link to="/book" className="btn-primary">Book Your Assessment</Link>
        <WhatsAppButton>Ask us a question</WhatsAppButton>
      </div>
    </div>
  );
}

export function Faqs() {
  return (
    <div className="container-wide py-16 max-w-4xl">
      <Seo
        title="Physiotherapy FAQs | Physio Hyve Clinic"
        description="Answers to what patients most often ask about physiotherapy, sessions, referrals and treatment at Physio Hyve Clinic, Nangloi."
        path="/faqs"
        jsonLd={faqLd(FAQS)}
      />
      <div className="eyebrow mb-3">Frequently asked questions</div>
      <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">Answers to what most patients ask.</h1>
      <div className="mt-10 space-y-3">
        {FAQS.map((f) => (
          <details key={f.q} className="card-soft p-5 group" data-testid={`faq-${f.q.slice(0,20)}`}>
            <summary className="cursor-pointer list-none flex items-center justify-between font-medium text-brand-ink">
              <span>{f.q}</span>
              <span className="text-brand-terracotta group-open:rotate-45 transition-transform">+</span>
            </summary>
            <p className="mt-3 text-brand-ink2 text-sm leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}

export function PatientStories() {
  const stories = [
    { name: "Ramesh, 34", title: "Back on the cricket field 9 months after ACL surgery", body: "Structured pre-op rehab, followed by staged strength, running and return-to-sport testing." },
    { name: "Priya, 41", title: "Two years of lower back pain — resolved", body: "A gradual load-management plan combined with targeted strength work restored full function." },
    { name: "Anil's father, 67", title: "Walking with a stick after stroke", body: "Family-supported neuro rehab focused on sit-to-stand, balance and confident short-distance walking." },
  ];
  return (
    <div className="container-wide py-16">
      <div className="max-w-2xl">
        <div className="eyebrow mb-3">Patient stories</div>
        <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">Real journeys of recovery.</h1>
        <p className="text-brand-ink2 mt-5">Identifying details are used only with patient consent. These stories describe pathways of care — not guarantees.</p>
      </div>
      <div className="mt-10 grid md:grid-cols-3 gap-5">
        {stories.map((s) => (
          <div key={s.name} className="card-soft p-6" data-testid={`story-${s.name.replace(/\s/g,'-')}`}>
            <div className="text-xs uppercase tracking-widest text-brand-terracotta">{s.name}</div>
            <h3 className="font-heading text-xl text-brand-ink mt-2">{s.title}</h3>
            <p className="text-sm text-brand-ink2 mt-2">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
